import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paper, Box, Alert, Button, CircularProgress, Typography } from '@mui/material';
import PDFViewer from './PDFViewer';
import { fetchReportPdfLink, pdfErrorMessage, ReportPdfLink } from '../../services/reportPdf';

interface ReportPdfViewerProps {
  reportId: string;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

// Refresh the link in the background when less than this is left
const REFRESH_MARGIN_MS = 3 * 60 * 1000;
// A link with less than this left is not used for a new iframe load or a new tab
const MIN_VALID_MS = 30 * 1000;

const ReportPdfViewer: React.FC<ReportPdfViewerProps> = ({ reportId, currentPage = 1, totalPages, onPageChange }) => {
  const [link, setLink] = useState<ReportPdfLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  const linkRef = useRef<ReportPdfLink | null>(null);

  const mint = useCallback(async () => {
    const fresh = await fetchReportPdfLink(reportId);
    linkRef.current = fresh;
    return fresh;
  }, [reportId]);

  const load = useCallback(() => {
    let cancelled = false;
    linkRef.current = null;
    setLink(null);
    setError(null);
    mint()
      .then((l) => {
        if (!cancelled) setLink(l);
      })
      .catch(async (err) => {
        const msg = await pdfErrorMessage(err);
        if (!cancelled) setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [mint]);

  useEffect(() => load(), [load]);

  // Background refresh: updates only the ref, so the visible PDF is not reloaded. The interval
  // plus visibilitychange survive Safari's background-tab timer throttling.
  useEffect(() => {
    const tick = () => {
      const l = linkRef.current;
      if (l && l.expiresAt - Date.now() < REFRESH_MARGIN_MS) mint().catch(() => {});
    };
    const id = window.setInterval(tick, 60000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [mint]);

  // The iframe URL is re-chosen from the freshest link whenever the page changes (whatever caused
  // it) or a new link loads, never on a background refresh. Reading the ref here is deliberate;
  // if React drops the memo it only picks an even fresher link.
  const view = useMemo(
    () => ({ page: currentPage, url: (linkRef.current ?? link)?.url ?? null }),
    [currentPage, link]
  );

  const handlePageChange = async (page: number) => {
    const l = linkRef.current;
    if (!l || l.expiresAt - Date.now() < MIN_VALID_MS) {
      try {
        await mint();
      } catch (err) {
        setError(await pdfErrorMessage(err));
        return;
      }
    }
    onPageChange?.(page);
  };

  // Safari only allows window.open inside the click itself, so the tab is opened synchronously:
  // straight to a fresh link, or blank and pointed at the link once a new one is minted.
  const handleOpenInNewTab = () => {
    const l = linkRef.current;
    if (l && l.expiresAt - Date.now() > MIN_VALID_MS) {
      window.open(`${l.url}#page=${currentPage}`, '_blank');
      return;
    }
    const win = window.open('', '_blank');
    mint()
      .then((fresh) => {
        if (win) win.location.href = `${fresh.url}#page=${currentPage}`;
      })
      .catch(() => {
        win?.close();
      });
  };

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!link || !view.url) {
    return (
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2">Loading PDF...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <PDFViewer
      pdfUrl={view.url}
      currentPage={view.page}
      totalPages={totalPages}
      onPageChange={onPageChange ? handlePageChange : undefined}
      onOpenInNewTab={handleOpenInNewTab}
    />
  );
};

export default ReportPdfViewer;
